import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';

const TabButton = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['active', 'cutColor', 'mobileWidth', 'mobileHeight', 'clipPathPolygon'].includes(prop)
})<TabButtonProps>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${props => props.width};
  height: ${props => props.height};
  box-sizing: border-box;
  font-family: 'BPdotsSquares';
  font-style: normal;
  font-weight: 700;
  font-size: 15.9985px;
  letter-spacing: 0.055em;
  line-height: 15px;
  background: ${props => props.background};
  color: ${props => props.background !== 'transparent' ? '#01161D' : '#256E8F'};
  clip-path: polygon(${props => props.clipPathPolygon});
  &::before,
  &::after {
    content: '';
    position: absolute;
    background-color: ${props => props.cutColor};
    display: ${props => props.active ? 'block' : 'none'};
  }

  @media (max-width: 768px) {
    width: ${props => props.mobileWidth};
    height: ${props => props.mobileHeight};
  }
`;

const SvgBorder = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: fit-content;
`;

interface TabButtonProps {
  background: string;
  cutColor: string;
  width: string;
  height: string;
  mobileWidth: string;
  mobileHeight: string;
  active: boolean;
  clipPathPolygon: string; // accepts percentage string directly
}

interface TabComponentProps {
  background?: string;
  cutColor?: string;
  width?: string;
  height?: string;
  mobileWidth?: string;
  mobileHeight?: string;
  active?: boolean;
  style?: React.CSSProperties;
  parentStyle?: React.CSSProperties;
  children?: React.ReactNode;
  onClick?: () => void;
  color?: string;
  showBorder?: boolean; // controls border visibility
  strokeColor?: string;
  noCut?: boolean;
  isStaking?: boolean; // new prop
}

const clipPathPolygonSmall: [number, number][] = [
  [0, 30],
  [15, 0],
  [100, 0],
  [100, 70],
  [85, 100],
  [0, 100],
];

const clipPathPolygonMedium: [number, number][] = [
  [0, 30],
  [4, 0],
  [100, 0],
  [100, 70],
  [96, 100],
  [0, 100],
];

const clipPathPolygonLarge: [number, number][] = [
  [0, 30],
  [5, 0],
  [100, 0],
  [100, 70],
  [95, 100],
  [0, 100],
];

const TabComponent: React.FC<TabComponentProps> = ({
  background = 'transparent',
  cutColor = 'black',
  width = 'fit-content',
  height = '50px',
  mobileWidth = '80px',
  mobileHeight = '40px',
  active = true,
  parentStyle = {},
  style = {},
  color,
  onClick,
  children,
  showBorder = false,
  strokeColor = 'black',
  noCut = false,
  isStaking = false, // default value false
}) => {
  const tabButtonRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [choseClipPathPolygon, setChoseClipPathPolygon] = useState(clipPathPolygonMedium);

  // Measure dimensions and choose polygon shape
  useEffect(() => {
    if (tabButtonRef.current) {
      const { width } = tabButtonRef.current.getBoundingClientRect();
      setDimensions({ width, height: tabButtonRef.current.offsetHeight });

      // Determine polygon based on width
      if (width < 70) {
        setChoseClipPathPolygon(clipPathPolygonSmall);
      } else if (width >= 70 && width < 450) {
        setChoseClipPathPolygon(clipPathPolygonMedium);
      } else {
        setChoseClipPathPolygon(clipPathPolygonLarge);
      }
    }
  }, [children, width, height, background]);

  let clipPathPolygonPercentage = choseClipPathPolygon.map(([x, y]) => `${x}% ${y}%`).join(', ');
  if (noCut) {
    clipPathPolygonPercentage = '0% 0%, 100% 0%, 100% 100%, 0% 100%';
  }

  // Determine final dimensions for the SVG border.
  // Only if isStaking is true and screen is big (>768px) we enforce a min width of 276.
  const isBigScreen = typeof window !== 'undefined' ? window.innerWidth > 768 : true;
  const finalWidth = (isStaking && isBigScreen) ? Math.max(dimensions.width, 276) : dimensions.width;
  const finalHeight = dimensions.height;

  return (
    <div
      style={{
        position: 'relative',
        width: 'fit-content',
        height: 'fit-content',
        ...parentStyle,
      }}
      onClick={onClick}
    >
      <TabButton
        ref={tabButtonRef}
        background={background}
        cutColor={cutColor}
        width={width}
        height={height}
        mobileWidth={mobileWidth}
        mobileHeight={mobileHeight}
        active={active}
        clipPathPolygon={clipPathPolygonPercentage}
        style={{ ...style, color }}
      >
        {children}
      </TabButton>
      {showBorder && (
        <SvgBorder
          style={{ width: finalWidth, height: finalHeight }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${finalWidth} ${finalHeight}`}
        >
          <polygon
            points={choseClipPathPolygon
              .map(([x, y]) => `${(x / 100) * finalWidth},${(y / 100) * finalHeight}`)
              .join(' ')}
            stroke={strokeColor}
            fill="none"
            strokeWidth="2"
          />
        </SvgBorder>
      )}
    </div>
  );
};

export default TabComponent;
